/**
 * Client for the release-note endpoints in xplatform_core_service.
 *
 * Reads take a BeeHive token but no module permission, so every authenticated
 * user reaches them. The authoring calls under /admin require
 * `admin.releasenotes.manage` and will 403 without it.
 */

import type {
  Module,
  Paginated,
  PublicReleaseNote,
  ReleaseNote,
  ReleaseNoteCreate,
  ReleaseNoteUpdate,
  ReleaseStatus,
  UnseenReleases,
} from "@models/releaseNotes";

const BASE_URL = import.meta.env.VITE_XPLATFORM_CORE_API_BASE_URL ?? "";

let getAccessToken: (() => Promise<string | null>) | null = null;

export const setReleaseNotesAccessTokenFn = (
  fn: (() => Promise<string | null>) | null,
): void => {
  getAccessToken = fn;
};

async function requireAccessToken(path: string): Promise<string> {
  const token = getAccessToken ? await getAccessToken() : null;
  if (!token) throw new ReleaseNotesApiError(401, path);
  return token;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Every release-notes route is protected by the API Gateway authorizer.
  // Resolve auth before calling fetch so a component mounting before BeeHive
  // is ready cannot emit a guaranteed 401 request without a bearer token.
  const token = await requireAccessToken(path);
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    throw new ReleaseNotesApiError(response.status, path);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export class ReleaseNotesApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly path: string,
  ) {
    super(`Release notes request failed: ${status} ${path}`);
    this.name = "ReleaseNotesApiError";
  }
}

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : "";
}

export const releaseNotesApi = {
  list(options: { module?: Module; limit?: number; offset?: number } = {}) {
    return request<Paginated<PublicReleaseNote>>(
      `/release-notes${query({
        module: options.module,
        limit: options.limit ?? 100,
        offset: options.offset,
      })}`,
    );
  },

  latest(limit = 5) {
    return request<PublicReleaseNote[]>(
      `/release-notes/latest${query({ limit })}`,
    );
  },

  unseen() {
    return request<UnseenReleases>("/release-notes/unseen");
  },

  /**
   * Acknowledge everything up to `upTo`. Pass the `latest_released_at` the
   * unseen call returned, never a locally generated timestamp.
   */
  markSeen(upTo: string) {
    return request<void>("/release-notes/seen", {
      method: "POST",
      body: JSON.stringify({ up_to: upTo }),
    });
  },

  admin: {
    list(
      options: {
        module?: Module;
        status?: ReleaseStatus;
        limit?: number;
        offset?: number;
      } = {},
    ) {
      return request<Paginated<ReleaseNote>>(
        `/admin/release-notes${query({
          module: options.module,
          status: options.status,
          limit: options.limit ?? 100,
          offset: options.offset,
        })}`,
      );
    },

    create(payload: ReleaseNoteCreate) {
      return request<ReleaseNote>("/admin/release-notes", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },

    update(module: Module, version: string, payload: ReleaseNoteUpdate) {
      return request<ReleaseNote>(`/admin/release-notes/${module}/${version}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
    },

    publish(module: Module, version: string) {
      return request<ReleaseNote>(
        `/admin/release-notes/${module}/${version}/publish`,
        { method: "POST" },
      );
    },

    unpublish(module: Module, version: string) {
      return request<ReleaseNote>(
        `/admin/release-notes/${module}/${version}/unpublish`,
        { method: "POST" },
      );
    },

    remove(module: Module, version: string) {
      return request<void>(`/admin/release-notes/${module}/${version}`, {
        method: "DELETE",
      });
    },
  },
};
